import React, { useState, useEffect } from 'react';
import { 
  Smartphone, Download, Bell, BellOff, Wifi, WifiOff,
  HardDrive, Trash2, RefreshCw, CheckCircle, XCircle,
  AlertCircle, Info
} from 'lucide-react';
import Button from '../common/Button';
import {
  canInstall,
  promptInstall,
  isInstalled,
  requestNotificationPermission,
  getNotificationPermission,
  subscribeToPushNotifications,
  unsubscribeFromPushNotifications,
  isOnline,
  addOnlineListener,
  addOfflineListener,
  getCacheSize,
  clearCache,
  registerBackgroundSync
} from '../../utils/pwa';
import { logger } from '../../utils/logger';

const PWATab = () => {
  const [pwaInstalled, setPwaInstalled] = useState(false);
  const [pwaInstallable, setPwaInstallable] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState(null);
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [online, setOnline] = useState(true);
  const [cacheInfo, setCacheInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    // Vérifier l'état initial
    checkPWAStatus();
    
    // Écouter les changements de connexion
    const removeOnlineListener = addOnlineListener(() => {
      setOnline(true);
      setMessage({ type: 'success', text: 'Connexion rétablie' });
      setTimeout(() => setMessage(''), 3000);
    });
    
    const removeOfflineListener = addOfflineListener(() => {
      setOnline(false);
      setMessage({ type: 'warning', text: 'Mode hors ligne activé' });
    });

    // Mise à jour des infos de cache
    updateCacheInfo();
    const interval = setInterval(updateCacheInfo, 30000); // Toutes les 30s

    return () => {
      removeOnlineListener();
      removeOfflineListener();
      clearInterval(interval);
    };
  }, []);

  const checkPWAStatus = async () => {
    setPwaInstalled(isInstalled());
    setPwaInstallable(canInstall());
    setNotificationPermission(getNotificationPermission());
    setOnline(isOnline());

    // Vérifier l'abonnement push
    if ('serviceWorker' in navigator) {
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration) {
        const subscription = await registration.pushManager.getSubscription();
        setPushSubscribed(!!subscription);
      }
    }
  };

  const updateCacheInfo = async () => {
    const info = await getCacheSize();
    setCacheInfo(info);
  };

  const handleInstallPWA = async () => {
    setLoading(true);
    const result = await promptInstall();
    if (result) {
      setMessage({ type: 'success', text: 'Application installée avec succès !' });
      setPwaInstalled(true);
      setPwaInstallable(false);
    } else {
      setMessage({ type: 'error', text: 'Installation annulée' });
    }
    setLoading(false);
  };

  const handleRequestNotifications = async () => {
    setLoading(true);
    const permission = await requestNotificationPermission();
    
    if (permission === 'granted') {
      setMessage({ type: 'success', text: 'Notifications autorisées' });
      setNotificationPermission('granted');
      
      // S'abonner aux notifications push
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration) {
        const subscription = await subscribeToPushNotifications(registration);
        if (subscription) {
          setPushSubscribed(true);
          // TODO: Envoyer l'abonnement au serveur Firebase
          logger.log('Subscription:', subscription);
        }
      }
    } else if (permission === 'denied') {
      setMessage({ 
        type: 'error', 
        text: 'Notifications refusées. Modifiez les paramètres de votre navigateur.' 
      });
      setNotificationPermission('denied');
    }
    
    setLoading(false);
  };

  const handleUnsubscribeNotifications = async () => {
    setLoading(true);
    const registration = await navigator.serviceWorker.getRegistration();
    const result = await unsubscribeFromPushNotifications(registration);
    
    if (result) {
      setMessage({ type: 'success', text: 'Désabonné des notifications' });
      setPushSubscribed(false);
    }
    
    setLoading(false);
  };

  const handleClearCache = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir vider le cache ? Cela supprimera les données hors ligne.')) {
      return;
    }

    setLoading(true);
    const result = await clearCache();
    
    if (result) {
      setMessage({ type: 'success', text: 'Cache vidé avec succès' });
      await updateCacheInfo();
    } else {
      setMessage({ type: 'error', text: 'Erreur lors du vidage du cache' });
    }
    
    setLoading(false);
  };

  const handleSyncData = async () => {
    setLoading(true);
    const registration = await navigator.serviceWorker.getRegistration();
    
    if (registration) {
      const result = await registerBackgroundSync(registration);
      if (result) {
        setMessage({ type: 'success', text: 'Synchronisation lancée' });
      } else {
        setMessage({ type: 'info', text: 'Synchronisation non disponible' });
      }
    }
    
    setLoading(false);
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div>
        <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-2 flex items-center gap-2">
          <Smartphone size={20} />
          Application Progressive (PWA)
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Installez l'application sur votre appareil et profitez du mode hors ligne
        </p>
      </div>

      {/* Messages */}
      {message && (
        <div className={`p-4 rounded-lg border ${
          message.type === 'success' 
            ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-800 dark:text-green-200'
            : message.type === 'warning'
            ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-200'
            : message.type === 'info'
            ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200'
            : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-800 dark:text-red-200'
        }`}>
          <p className="text-sm font-medium">{message.text}</p>
        </div>
      )}

      {/* Statut connexion */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {online ? (
              <Wifi className="text-green-600 dark:text-green-400" size={24} />
            ) : (
              <WifiOff className="text-red-600 dark:text-red-400" size={24} />
            )}
            <div>
              <h4 className="font-semibold text-gray-800 dark:text-white">
                {online ? 'En ligne' : 'Hors ligne'}
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {online 
                  ? 'Connexion active - toutes les fonctionnalités disponibles'
                  : 'Mode hors ligne - données en cache disponibles'
                }
              </p>
            </div>
          </div>
          <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
            online 
              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
              : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
          }`}>
            {online ? 'Connecté' : 'Déconnecté'}
          </div>
        </div>
      </div>

      {/* Installation PWA */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-start gap-4">
          <div className={`p-3 rounded-full ${
            pwaInstalled 
              ? 'bg-green-100 dark:bg-green-900/30'
              : 'bg-blue-100 dark:bg-blue-900/30'
          }`}>
            <Download 
              className={pwaInstalled 
                ? 'text-green-600 dark:text-green-400'
                : 'text-blue-600 dark:text-blue-400'
              } 
              size={24} 
            />
          </div>

          <div className="flex-1">
            <h4 className="font-semibold text-gray-800 dark:text-white mb-1 flex items-center gap-2">
              Installation sur l'appareil
              {pwaInstalled && (
                <CheckCircle className="text-green-600 dark:text-green-400" size={16} />
              )}
            </h4>
            
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {pwaInstalled 
                ? 'Application installée ! Accédez-y depuis votre écran d\'accueil.'
                : 'Installez l\'application sur votre appareil pour un accès rapide et hors ligne.'
              }
            </p>

            {pwaInstallable && !pwaInstalled && (
              <Button
                onClick={handleInstallPWA}
                isLoading={loading}
                disabled={loading}
                icon={Download}
              >
                Installer l'application
              </Button>
            )}

            {!pwaInstallable && !pwaInstalled && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  📱 L'application est déjà installée ou l'installation n'est pas disponible sur cet appareil.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Notifications Push */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-start gap-4">
          <div className={`p-3 rounded-full ${
            notificationPermission === 'granted'
              ? 'bg-green-100 dark:bg-green-900/30'
              : 'bg-gray-100 dark:bg-gray-700'
          }`}>
            {notificationPermission === 'granted' ? (
              <Bell className="text-green-600 dark:text-green-400" size={24} />
            ) : (
              <BellOff className="text-gray-600 dark:text-gray-400" size={24} />
            )}
          </div>

          <div className="flex-1">
            <h4 className="font-semibold text-gray-800 dark:text-white mb-1">
              Notifications Push
            </h4>
            
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {notificationPermission === 'granted'
                ? 'Recevez des notifications pour les nouveaux programmes, anniversaires et événements.'
                : 'Activez les notifications pour ne rien manquer des actualités de l\'église.'
              }
            </p>

            {notificationPermission === 'default' && (
              <Button
                onClick={handleRequestNotifications}
                isLoading={loading}
                disabled={loading}
                icon={Bell}
              >
                Activer les notifications
              </Button>
            )}

            {notificationPermission === 'granted' && (
              <div className="flex gap-3">
                <Button
                  onClick={handleUnsubscribeNotifications}
                  isLoading={loading}
                  disabled={loading || !pushSubscribed}
                  variant="outline"
                  icon={BellOff}
                >
                  Désactiver
                </Button>
                <div className="px-3 py-2 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-lg text-sm font-medium">
                  ? Activées
                </div>
              </div>
            )}

            {notificationPermission === 'denied' && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                <p className="text-sm text-red-800 dark:text-red-200">
                  ? Notifications bloquées. Modifiez les paramètres de votre navigateur pour les activer.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Cache et stockage */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-full">
            <HardDrive className="text-purple-600 dark:text-purple-400" size={24} />
          </div>

          <div className="flex-1">
            <h4 className="font-semibold text-gray-800 dark:text-white mb-1">
              Stockage et Cache
            </h4>
            
            {cacheInfo && (
              <div className="mb-4">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                  Espace utilisé : <strong>{formatBytes(cacheInfo.usage)}</strong> sur {formatBytes(cacheInfo.quota)} ({cacheInfo.percentage}%)
                </p>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div 
                    className="bg-purple-600 h-2 rounded-full transition-all"
                    style={{ width: `${Math.min(cacheInfo.percentage, 100)}%` }}
                  />
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <Button
                onClick={handleSyncData}
                isLoading={loading}
                disabled={loading || !online}
                icon={RefreshCw}
                variant="outline"
              >
                Synchroniser
              </Button>
              <Button
                onClick={handleClearCache}
                isLoading={loading}
                disabled={loading}
                icon={Trash2}
                variant="outline"
              >
                Vider le cache
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Informations */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Info className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" size={20} />
          <div className="text-sm text-blue-800 dark:text-blue-200">
            <p className="font-semibold mb-1">Mode hors ligne</p>
            <p>
              Les données consultées sont automatiquement mises en cache pour une utilisation hors ligne. 
              Vos modifications seront synchronisées dès que vous serez reconnecté.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PWATab;
